import { useEffect, useRef, useState } from 'react';
import { getRentalDocuments } from '@/services/api/rentals';
import type { CorrectedDocument } from '@/services/api/opsHandovers';
import type { RentalDocuments } from '@/types/rental';
import { activeReissue } from './rentalDisplay';

/** How often the documents block is re-read while a reissue renders. */
export const REISSUE_POLL_MS = 5_000;

interface Options {
  rentalID: string | undefined;
  /** The rental's documents block as the page holds it. */
  documents: RentalDocuments | null | undefined;
  /** A fresh documents block, for the page to merge into its detail. */
  onDocuments: (documents: RentalDocuments) => void;
  /** Every pending copy has re-rendered. */
  onLanded: (documents: CorrectedDocument[]) => void;
  /** The wait ran out with copies still pending: the task is presumed lost. */
  onGaveUp: (documents: CorrectedDocument[]) => void;
}

/**
 * Follows a checklist correction's reissue from the server's own account of
 * it (`documents.reissue`), so a reload or a second viewer sees the same
 * state. While copies are pending it re-reads only the documents block, and
 * not while the tab is hidden.
 */
export const useDocumentReissueWatch = ({
  rentalID,
  documents,
  onDocuments,
  onLanded,
  onGaveUp,
}: Options) => {
  const pending = activeReissue(documents);
  const pendingKey = pending.join(',');
  const serverPending = documents?.reissue?.pending ?? [];
  // Re-arms the poll after a read that changed nothing.
  const [tick, setTick] = useState(0);
  const callbacks = useRef({ onDocuments, onLanded, onGaveUp });
  callbacks.current = { onDocuments, onLanded, onGaveUp };
  // Everything this page saw pending, so a landing names every copy, not
  // only the ones that landed last.
  const watched = useRef<CorrectedDocument[]>([]);

  useEffect(() => {
    if (pending.length > 0) {
      watched.current = Array.from(new Set([...watched.current, ...pending]));
      return;
    }
    if (watched.current.length === 0) return;
    const was = watched.current;
    watched.current = [];
    if (serverPending.length === 0) callbacks.current.onLanded(was);
    else callbacks.current.onGaveUp(serverPending);
    // Keyed on the pending set, not on every array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey]);

  useEffect(() => {
    if (!rentalID || !pendingKey) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (document.visibilityState !== 'hidden') {
        try {
          const next = await getRentalDocuments(rentalID);
          if (!cancelled) callbacks.current.onDocuments(next);
        } catch {
          // A failed read is just another wait.
        }
      }
      if (!cancelled) setTick((t) => t + 1);
    }, REISSUE_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [rentalID, pendingKey, tick]);

  return {
    pending,
    isReissuing: (document: string) =>
      (pending as string[]).includes(document),
  };
};
